<template>
  <header class="nav" :class="{ scrolled: scrolled || open }">
    <div class="container nav-inner">
      <router-link :to="{ path: '/home' }" class="brand" aria-label="白泽数字科技 BAIZE" @click="open = false">
        <span class="brand-word">BAIZE</span>
      </router-link>

      <nav class="menu" aria-label="主导航">
        <router-link
          v-for="item in menus"
          :key="item.label"
          :to="item.path"
          class="menu-link"
          :class="{ active: isActive(item) }"
        >{{ item.label }}</router-link>
      </nav>

      <div class="nav-right">
        <router-link to="/download" class="btn nav-cta">
          AI管家
        </router-link>
        <button class="burger" :class="{ open }" aria-label="菜单" @click="open = !open">
          <span /><span /><span />
        </button>
      </div>
    </div>

    <!-- 移动端下拉 -->
    <transition name="drop">
      <nav v-if="open" class="mobile-menu" aria-label="移动导航">
        <router-link
          v-for="item in menus"
          :key="item.label"
          :to="item.path"
          class="mobile-link"
          :class="{ active: isActive(item) }"
          @click="open = false"
        >{{ item.label }}</router-link>
        <router-link to="/download" class="btn nav-cta" @click="open = false">AI管家</router-link>
      </nav>
    </transition>
  </header>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'

/* 五大内容页，每个导航项独立成页 */
const menus = [
  { label: '首页', path: '/home' },
  { label: 'APP下载', path: '/download' },
  { label: '发展路径', path: '/path' },
  { label: '联合开发', path: '/coop' },
  { label: '小伙伴们', path: '/team' }
]

const route = useRoute()
const open = ref(false)
const scrolled = ref(false)

const isActive = item => route.path === item.path

const onScroll = () => {
  scrolled.value = window.scrollY > 24
}

watch(() => route.path, () => {
  open.value = false
  window.scrollTo({ top: 0 })
})

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()
})
onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))
</script>

<style scoped>
.nav {
  position: fixed;
  inset: 0 0 auto 0;
  height: var(--nav-h);
  z-index: 100;
  display: flex;
  align-items: center;
  transition: background 0.35s ease, border-color 0.35s ease, backdrop-filter 0.35s ease, box-shadow 0.35s ease;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(31, 59, 120, 0.08);
}
.nav.scrolled {
  background: rgba(255, 255, 255, 0.92);
  border-bottom-color: var(--c-line);
  box-shadow: 0 12px 34px -22px rgba(35, 66, 133, 0.28);
}
.nav-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

/* Brand */
.brand {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.brand-word {
  display: inline-block;
  font-size: 23px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 6px;
  text-indent: 6px;
  color: #000;
  transition: opacity 0.25s ease;
}
.brand:hover .brand-word {
  opacity: 0.82;
}

/* Desktop menu */
.menu {
  display: flex;
  align-items: center;
  gap: 6px;
}
.menu-link {
  position: relative;
  padding: 8px 16px;
  font-size: 15.5px;
  color: var(--c-sub);
  border-radius: 10px;
  transition: color 0.25s ease;
}
.menu-link::after {
  content: "";
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 2px;
  height: 2px;
  border-radius: 2px;
  background: var(--grad-main);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.menu-link:hover {
  color: var(--c-txt);
}
.menu-link:hover::after {
  transform: scaleX(0.4);
}
.menu-link.active {
  color: var(--brand);
}
.menu-link.active::after {
  transform: scaleX(1);
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* 首屏同款深色胶囊按钮 */
.nav-cta {
  height: 44px;
  padding: 0 26px;
  font-size: 14.5px;
  color: #fff;
  background: #131c3b;
  box-shadow: 0 10px 26px -10px rgba(19, 28, 59, 0.6);
}
.nav-cta:hover {
  transform: translateY(-2px);
  background: #1c2a55;
  box-shadow: 0 16px 34px -12px rgba(19, 28, 59, 0.7);
}

.burger {
  display: none;
  width: 42px;
  height: 42px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border-radius: 10px;
  border: 1px solid var(--c-line);
}
.burger span {
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: var(--c-txt);
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.burger.open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}
.burger.open span:nth-child(2) {
  opacity: 0;
}
.burger.open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* Mobile menu */
.mobile-menu {
  position: absolute;
  top: var(--nav-h);
  left: 0;
  right: 0;
  padding: 14px 5% 26px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--c-line);
  box-shadow: 0 30px 60px -20px rgba(35, 66, 133, 0.28);
}
.mobile-link {
  padding: 13px 16px;
  font-size: 16px;
  border-radius: 12px;
  color: var(--c-txt);
  border-bottom: 1px solid rgba(31, 59, 120, 0.08);
}
.mobile-link:hover,
.mobile-link.active {
  color: var(--brand);
  background: rgba(47, 102, 238, 0.07);
}
.mobile-link:active {
  background: rgba(47, 102, 238, 0.12);
}
.mobile-menu .nav-cta {
  margin-top: 12px;
}

/* Transition */
.drop-enter-active,
.drop-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.drop-enter-from,
.drop-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

@media (max-width: 920px) {
  .menu,
  .nav-right .nav-cta {
    display: none;
  }
  .burger {
    display: inline-flex;
  }
}
</style>
